import React from "react";
import LegalPage from "./LegalPage";
import data from "@content/legal/contact.json";
import company from "@content/legal/company.json";
import { fillTokens } from "./legalUtils";

const socialIcons = {
    instagram: "M12 2.2c3.2 0 3.6 0 4.8.1 1.2.1 2 .3 2.6.5.7.3 1.3.6 1.9 1.2s1 1.2 1.2 1.9c.2.6.4 1.4.5 2.6.1 1.2.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 1.2-.3 2-.5 2.6-.3.7-.6 1.3-1.2 1.9s-1.2 1-1.9 1.2c-.6.2-1.4.4-2.6.5-1.2.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-1.2-.1-2-.3-2.6-.5a5 5 0 0 1-1.9-1.2 5 5 0 0 1-1.2-1.9c-.2-.6-.4-1.4-.5-2.6C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8c.1-1.2.3-2 .5-2.6.3-.7.6-1.3 1.2-1.9s1.2-1 1.9-1.2c.6-.2 1.4-.4 2.6-.5C8.4 2.2 8.8 2.2 12 2.2Zm0 1.8c-3.2 0-3.5 0-4.7.1-1.1.1-1.7.2-2.1.4-.5.2-.9.4-1.3.8-.4.4-.6.8-.8 1.3-.2.4-.3 1-.4 2.1-.1 1.2-.1 1.5-.1 4.7s0 3.5.1 4.7c.1 1.1.2 1.7.4 2.1.2.5.4.9.8 1.3.4.4.8.6 1.3.8.4.2 1 .3 2.1.4 1.2.1 1.5.1 4.7.1s3.5 0 4.7-.1c1.1-.1 1.7-.2 2.1-.4.5-.2.9-.4 1.3-.8.4-.4.6-.8.8-1.3.2-.4.3-1 .4-2.1.1-1.2.1-1.5.1-4.7s0-3.5-.1-4.7c-.1-1.1-.2-1.7-.4-2.1-.2-.5-.4-.9-.8-1.3a3.3 3.3 0 0 0-1.3-.8c-.4-.2-1-.3-2.1-.4-1.2-.1-1.5-.1-4.7-.1Zm0 3.1a4.9 4.9 0 1 1 0 9.8 4.9 4.9 0 0 1 0-9.8Zm0 8.1a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Zm5.1-8.3a1.2 1.2 0 1 1 0-2.3 1.2 1.2 0 0 1 0 2.3Z",
    youtube: "M23 7.2a3 3 0 0 0-2.1-2.1C19 4.5 12 4.5 12 4.5s-7 0-8.9.6A3 3 0 0 0 1 7.2 31.5 31.5 0 0 0 .5 12 31.5 31.5 0 0 0 1 16.8a3 3 0 0 0 2.1 2.1c1.9.6 8.9.6 8.9.6s7 0 8.9-.6a3 3 0 0 0 2.1-2.1 31.5 31.5 0 0 0 .5-4.8 31.5 31.5 0 0 0-.5-4.8ZM9.8 15.6V8.4l6.3 3.6-6.3 3.6Z",
    linkedin: "M4.98 3.5a2.5 2.5 0 1 1 0 5.001 2.5 2.5 0 0 1 0-5.001ZM3 9h4v12H3V9Zm7 0h3.8v1.71h.05a4.16 4.16 0 0 1 3.75-2.06c4.01 0 4.75 2.64 4.75 6.07V21h-4v-5.31c0-1.27-.02-2.9-1.77-2.9-1.77 0-2.04 1.38-2.04 2.81V21h-4V9Z",
    facebook: "M13.4 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.6-1.5h1.6V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4v2.4H7.5V14h2.6v8h3.3Z",
};

const ContactUs = () => {
    const filled = fillTokens(data);
    const channels = filled.channels || [];

    const socialLinks = [
        company.instagram && { key: "instagram", href: company.instagram, label: "Instagram" },
        company.youtube && { key: "youtube", href: company.youtube, label: "YouTube" },
        company.linkedin && { key: "linkedin", href: company.linkedin, label: "LinkedIn" },
        company.facebook && { key: "facebook", href: company.facebook, label: "Facebook" },
    ].filter(Boolean);

    const digitsOnly = (company.supportPhone || "").replace(/\D/g, "");

    const buildAction = (action, value) => {
        if (action === "email") return `mailto:${value}`;
        if (action === "call") return `tel:+${digitsOnly}`;
        return value;
    };

    return (
        <LegalPage slug="contact-us" data={filled} crumbLabel="Contact Us">
            <div className="grid md:grid-cols-2 gap-3">
                {channels.map((c) => (
                    <a
                        key={c.label}
                        href={buildAction(c.action, c.value)}
                        className="panel panel-hover p-5 flex flex-col gap-2 no-underline"
                        data-testid={`contact-${c.action}`}
                    >
                        <div className="font-mono text-[11px] uppercase tracking-widest text-brand">
                            {c.label}
                        </div>
                        <div className="font-display text-xl font-bold text-slate-900 break-all">
                            {c.value}
                        </div>
                        {c.helper && (
                            <div className="text-sm text-dim leading-snug">
                                {c.helper}
                            </div>
                        )}
                        <div className="mt-2 inline-flex items-center gap-2 text-brand font-mono text-xs tracking-wide">
                            {c.action === "email" ? "Open email client →" : "Start call →"}
                        </div>
                    </a>
                ))}
            </div>

            {filled.office && (
                <article className="panel p-5 md:p-6">
                    <h2 className="font-display text-xl md:text-2xl font-bold text-slate-900 leading-tight">
                        {filled.office.heading}
                    </h2>
                    <p className="mt-3 text-[15px] text-dim leading-relaxed">
                        {filled.office.address}
                    </p>
                    {filled.office.mapUrl && (
                        <a
                            href={filled.office.mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand hover:underline"
                            data-testid="contact-map"
                        >
                            View on Google Maps →
                        </a>
                    )}
                    {filled.office.hours && (
                        <div className="mt-5 pt-4 border-t border-[var(--border)]">
                            <div className="font-mono text-[11px] uppercase tracking-widest text-brand">
                                {filled.office.hoursHeading}
                            </div>
                            <div className="mt-1.5 text-slate-900">
                                {filled.office.hours}
                            </div>
                        </div>
                    )}
                </article>
            )}

            {socialLinks.length > 0 && (
                <article className="panel p-5 md:p-6">
                    <h2 className="font-display text-xl md:text-2xl font-bold text-slate-900 leading-tight">
                        {filled.socialHeading}
                    </h2>
                    <ul className="mt-4 flex flex-wrap gap-2.5" role="list">
                        {socialLinks.map((s) => (
                            <li key={s.key}>
                                <a
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={s.label}
                                    className="w-11 h-11 rounded-lg border border-[var(--border)] bg-white flex items-center justify-center text-slate-600 hover:text-brand hover:border-[rgba(124,231,59,0.5)] transition-colors"
                                    data-testid={`contact-social-${s.key}`}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                        <path d={socialIcons[s.key]} />
                                    </svg>
                                </a>
                            </li>
                        ))}
                    </ul>
                </article>
            )}

            {filled.note && (
                <p className="text-xs text-mute font-mono tracking-wide leading-relaxed">
                    {filled.note}
                </p>
            )}
        </LegalPage>
    );
};

export default ContactUs;
